import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  UserCircleIcon,
  DocumentTextIcon,
  ReceiptRefundIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';

// Mock data for demonstration
const mockUserData = {
  name: 'John Doe',
  employeeId: 'EMP-12345',
  department: 'Software Development',
  jobTitle: 'Senior React Developer',
  email: 'john.doe@example.com',
  phone: '9876543210',
  address: '123, Tech Park, City, Country',
};

const mockPaystubs = [
  { id: 1, date: 'October 2025', url: '/documents/paystub-oct-25.pdf' },
  { id: 2, date: 'September 2025', url: '/documents/paystub-sep-25.pdf' },
  { id: 3, date: 'August 2025', url: '/documents/paystub-aug-25.pdf' },
];

const mockRequests = [
  { id: uuidv4(), type: 'Leave Request', status: 'Pending', date: '2025-10-20', details: 'Medical leave for 3 days' },
  { id: uuidv4(), type: 'IT Support', status: 'Approved', date: '2025-10-15', details: 'Laptop screen replacement' },
];

// Reusable components for a clean look
const Card = ({ children, icon, title, ...props }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-white p-6 rounded-2xl shadow-xl flex flex-col items-start hover:shadow-2xl transition-shadow duration-300"
    {...props}
  >
    <div className="flex items-center space-x-3 mb-4">
      {React.cloneElement(icon, { className: 'w-8 h-8 text-indigo-500' })}
      <h3 className="text-xl font-bold text-gray-800">{title}</h3>
    </div>
    {children}
  </motion.div>
);

const EmployeeESS = () => {
  const [activeRequest, setActiveRequest] = useState(null);
  const [requests, setRequests] = useState(mockRequests);

  const handleNewRequest = (requestType) => {
    // In a real app, this would open a modal with a form
    const newRequest = {
      id: uuidv4(),
      type: requestType,
      status: 'Pending',
      date: moment().format('YYYY-MM-DD'),
      details: `New ${requestType} submitted.`,
    };
    setRequests([newRequest, ...requests]);
    alert(`${requestType} submitted successfully!`);
  };

  const RequestModal = ({ onClose }) => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
    >
      <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-lg w-full">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Submit a Request</h2>
        <p className="text-gray-600 mb-6">This is a placeholder for a request form. You can select a type and submit.</p>
        <div className="flex justify-end space-x-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-2 rounded-full bg-gray-200 text-gray-700 font-semibold"
            onClick={onClose}
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-2 rounded-full bg-indigo-600 text-white font-semibold"
            onClick={() => { handleNewRequest('Custom Request'); onClose(); }}
          >
            Submit
          </motion.button>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-100 p-8 text-gray-800">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center max-w-4xl mx-auto mb-10"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold text-indigo-800">Employee Self-Service Portal</h1>
        <p className="text-lg md:text-xl font-light text-indigo-600 mt-2">
          Your personal dashboard to manage information and requests.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
        {/* Profile Card */}
        <Card icon={<UserCircleIcon />} title="My Profile" className="lg:col-span-1">
          <ul className="text-sm space-y-2 text-gray-600">
            <li><span className="font-semibold">Employee ID:</span> {mockUserData.employeeId}</li>
            <li><span className="font-semibold">Job Title:</span> {mockUserData.jobTitle}</li>
            <li><span className="font-semibold">Department:</span> {mockUserData.department}</li>
            <li><span className="font-semibold">Email:</span> {mockUserData.email}</li>
            <li><span className="font-semibold">Phone:</span> {mockUserData.phone}</li>
          </ul>
        </Card>

        {/* Documents Card */}
        <Card icon={<DocumentTextIcon />} title="My Documents" className="lg:col-span-1">
          <ul className="text-sm space-y-2 text-gray-600 w-full">
            {mockPaystubs.map(doc => (
              <li key={doc.id} className="flex justify-between items-center bg-gray-50 p-3 rounded-lg hover:bg-gray-100 transition-colors">
                <span>Paystub - {doc.date}</span>
                <a href={doc.url} download className="text-indigo-500 hover:text-indigo-700 transition-colors">Download</a>
              </li>
            ))}
          </ul>
        </Card>

        {/* Request Submission Card */}
        <Card icon={<TicketIcon />} title="Submit a Request" className="lg:col-span-1">
          <p className="text-sm text-gray-600 mb-4">Initiate new requests for HR or IT.</p>
          <div className="flex flex-col space-y-4 w-full">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="py-3 px-6 rounded-full bg-indigo-500 text-white font-semibold shadow-md hover:bg-indigo-600 transition-colors"
              onClick={() => handleNewRequest('Leave Request')}
            >
              Request Leave
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="py-3 px-6 rounded-full bg-purple-500 text-white font-semibold shadow-md hover:bg-purple-600 transition-colors"
              onClick={() => handleNewRequest('IT Support')}
            >
              IT Support Ticket
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="py-3 px-6 rounded-full bg-pink-500 text-white font-semibold shadow-md hover:bg-pink-600 transition-colors"
              onClick={() => handleNewRequest('General Query')}
            >
              General Query
            </motion.button>
          </div>
        </Card>

        {/* Request Status Card */}
        <Card icon={<ReceiptRefundIcon />} title="My Requests Status" className="md:col-span-2 lg:col-span-3">
          <div className="overflow-x-auto w-full">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <AnimatePresence>
                  {requests.length > 0 ? (
                    requests.map((req) => (
                      <motion.tr
                        key={req.id}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{req.type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moment(req.date).format('MMMM Do, YYYY')}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                            ${req.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {req.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{req.details}</td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No requests submitted yet.</td>
                    </tr>
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </Card>
      </div>
      <AnimatePresence>
        {activeRequest && <RequestModal onClose={() => setActiveRequest(null)} />}
      </AnimatePresence>
    </div>
  );
};

export default EmployeeESS;